import {
  ECouncilSessionState,
  ECouncilVotingResult,
  ECouncilVotingType,
  ICouncilSession,
} from "./ICouncil";

// Biuro rady, ETAP 2 — portal radnego widziany od strony administracyjnej:
// ustawienia portalu, kartoteka radnych, prowadzenie obrad i głosowania.
// Wartości enumów są 1:1 z Kwantum.Enums — nie wolno ich przenumerowywać.

export enum ECouncilorRole {
  /** Zwykły radny. */
  None = 0,
  President = 1,
  Deputy = 2,
}

export enum ECouncilSpeakType {
  Normal = 0,
  AdVocem = 1,
  /** W kwestii proceduralnej. */
  Formal = 2,
}

/**
 * Rodzaj wpisu w dzienniku przebiegu sesji. Numeracja 1:1 z Magistratem
 * (CouncilSessionProtocolItemTypes) — nie wolno jej przenumerowywać.
 */
export enum ECouncilProtocolItemType {
  SessionBegin = 1,
  SessionEnd = 2,
  VotingBegin = 3,
  VotingEnd = 4,
  DiscussionBegin = 5,
  DiscussionEnd = 6,
  SessionSuspended = 7,
  VotingCancel = 8,
  NextStep = 10,
  SessionResumed = 11,
  LeaderChanged = 100,
  RecorderChanged = 101,
  ModeratorChanged = 102,
  PresenceCheck = 109,
  PersonPresent = 110,
  PersonAbsent = 111,
  PersonTookVote = 200,
  PersonSubmitAnRequest = 201,
  PersonSubmitAnObjection = 202,
  PersonSubmitAnInterpelation = 203,
  PersonSubmitAnQuestionn = 204,
  Other = 999,
}

export enum ECouncilVoteSource {
  /** Portal radnego. */
  Site = 0,
  /** Pad do głosowania na sali (w Nota nieużywane — zostaje dla zgodności danych). */
  Keypad = 1,
}

// ── Ustawienia portalu ──────────────────────────────────────────────────────

export interface ICouncilPortalSettings {
  Id: string;
  /** Identyfikator urzędu w adresie portalu: <portal>/{Slug}. */
  Slug: string | null;
  OfficeName: string | null;
  PortalHeader: string | null;
  /** Ustawowy skład rady — bez niego nie da się zamknąć głosowania. */
  TotalCouncilorsCount: number | null;
  /** Wariant większości bezwzględnej: AbsoluteVer1 albo AbsoluteVer2. */
  AbsoluteVotingType: ECouncilVotingType | null;
  SupportsStreaming: boolean;
  YouTubeChannel: string | null;

  // ── Transmisja: OBS Studio i kamery PTZ ───────────────────────────────────
  // Sekrety (hasło obs-websocket, hasła kamer) NIE przychodzą tą drogą — panel
  // dostaje wyłącznie flagi „ustawione". Jawne wartości pobiera wtyczka desktop,
  // bo to ona łączy się z OBS i z kamerami.

  /** Katalog na pliki napisów: pasek.txt, tytul.txt, mowca.txt. */
  ObsTextFilePath: string | null;
  /** OBS działa na tym samym komputerze co wtyczka. */
  ObsUseLocal: boolean;
  ObsAddress: string | null;
  HasObsPassword: boolean;
  ObsDefaultScene: string | null;
  ObsVotingScene: string | null;
  EnableDahuaIntegration: boolean;
  Cameras: ICouncilCamera[];

  IsEnabled: boolean;
  ActiveCouncilorsCount: number;
}

export interface ICouncilCameraPreset {
  Id: string;
  CameraId: string;
  Name: string | null;
  /** Numer presetu zaprogramowanego w kamerze. */
  Number: number;
  SortOrder: number;
}

export interface ICouncilCamera {
  Id: string;
  Number: number;
  /** „Kamera #N". */
  Label: string | null;
  Address: string | null;
  Login: string | null;
  HasPassword: boolean;
  /** Nazwa sceny OBS z obrazem tej kamery. */
  Scene: string | null;
  Presets: ICouncilCameraPreset[];
}

export interface ISaveCouncilCameraPresetRequest {
  Id?: string | null;
  Name: string;
  Number: number;
}

export interface ISaveCouncilCameraRequest {
  /** Puste = nowa kamera. */
  Id?: string | null;
  Address: string;
  Login?: string | null;
  /** Nowe hasło. Puste = bez zmiany. */
  Password?: string | null;
  RemovePassword?: boolean;
  Scene?: string | null;
  Presets: ISaveCouncilCameraPresetRequest[];
}

export interface ISaveCouncilPortalSettingsRequest {
  Slug: string;
  OfficeName?: string | null;
  PortalHeader?: string | null;
  TotalCouncilorsCount?: number | null;
  AbsoluteVotingType?: ECouncilVotingType | null;
  SupportsStreaming: boolean;
  YouTubeChannel?: string | null;

  ObsTextFilePath?: string | null;
  ObsUseLocal: boolean;
  ObsAddress?: string | null;
  /** Nowe hasło obs-websocket. Puste = bez zmiany. */
  ObsPassword?: string | null;
  /** Jawne skasowanie hasła — samo puste pole nic nie zmienia. */
  RemoveObsPassword?: boolean;
  ObsDefaultScene?: string | null;
  ObsVotingScene?: string | null;
  EnableDahuaIntegration: boolean;
  /** Całościowy zapis: kamery spoza listy zostają wycofane. */
  Cameras?: ISaveCouncilCameraRequest[];

  IsEnabled: boolean;
}

// ── Radni ───────────────────────────────────────────────────────────────────

export interface ICouncilCouncilor {
  Id: string;
  FirstName: string | null;
  LastName: string | null;
  Email: string | null;
  Phone: string | null;
  Login: string | null;
  Role: ECouncilorRole;
  Male: boolean;
  CadenceStart: string | null;
  CadenceEnd: string | null;
  IsActive: boolean;
  /** Hasło nadane — bez niego radny nie zaloguje się do portalu. */
  HasPassword: boolean;
  LastActivity: string | null;
  SortOrder: number;
  /** Kamera pokazująca tego radnego. Null = pierwsza skonfigurowana. */
  CameraId: string | null;
  /** Numer presetu w kamerze celujący w miejsce radnego. Null = bez najazdu. */
  CameraPreset: number | null;
}

export interface ISaveCouncilCouncilorRequest {
  Id?: string | null;
  FirstName?: string | null;
  LastName?: string | null;
  Email?: string | null;
  Phone?: string | null;
  Login: string;
  Role: ECouncilorRole;
  Male: boolean;
  CadenceStart?: string | null;
  CadenceEnd?: string | null;
  IsActive: boolean;
  /** Wypełnione tylko przy zakładaniu konta; przy edycji puste = bez zmiany. */
  Password?: string | null;
  CameraId?: string | null;
  CameraPreset?: number | null;
}

// ── Głosowania ──────────────────────────────────────────────────────────────

export interface ICouncilVotingOption {
  Id: string;
  Name: string | null;
  Order: number;
  Votes: number;
}

export interface ICouncilVote {
  Id: string;
  CouncilorId: string;
  CouncilorLabel: string | null;
  Value: ECouncilVotingResult | null;
  ValueLabel: string | null;
  OptionId: string | null;
  Source: ECouncilVoteSource;
  Date: string;
}

export interface ICouncilVoting {
  Id: string;
  SessionId: string;
  AgendaItemId: string;
  SessionNumber: string | null;
  SessionDate: string | null;
  AgendaItemOrder: string | null;
  AgendaItemTitle: string | null;
  Type: ECouncilVotingType;
  Description: string | null;
  StartDate: string | null;
  /** null = głosowanie trwa. */
  EndDate: string | null;
  CloseDate: string | null;
  Result: ECouncilVotingResult | null;
  OptionIdResult: string | null;
  ResultLabel: string | null;
  IsClassified: boolean;
  IsAnalog: boolean;
  RevotingForId: string | null;
  QualifiedNumerator: number | null;
  QualifiedDenominator: number | null;
  For: number;
  Against: number;
  Abstain: number;
  WithoutVote: number;
  Total: number;
  Options: ICouncilVotingOption[];
  /** Pusta dla głosowań tajnych i dopóki głosowanie trwa. */
  Votes: ICouncilVote[];
}

export interface IStartCouncilVotingRequest {
  AgendaItemId: string;
  Type: ECouncilVotingType;
  Description: string;
  IsClassified: boolean;
  QualifiedNumerator?: number | null;
  QualifiedDenominator?: number | null;
  RevotingForId?: string | null;

  /**
   * Akt prawa miejscowego, nad którym się głosuje. Backend przyjmuje wyłącznie akt
   * dołączony do tego punktu porządku obrad. Puste = głosowanie bez powiązania z aktem.
   */
  LegalActId?: string | null;

  Options: { Name: string; Order: number }[];
}

export interface ISaveAnalogCouncilVotingRequest {
  Id?: string | null;
  AgendaItemId: string;
  Type: ECouncilVotingType;
  Description: string;
  For: number;
  Against: number;
  Abstain: number;
  QualifiedNumerator?: number | null;
  QualifiedDenominator?: number | null;
}

// ── Prowadzenie obrad ───────────────────────────────────────────────────────

export interface ICouncilPresence {
  CouncilorId: string;
  FullName: string | null;
  Role: ECouncilorRole;
  IsPresent: boolean;
  /** Czy radny oddał głos w trwającym głosowaniu. */
  HasVoted: boolean;
  /** Do plakietki mówcy w napisach OBS: „Radny" albo „Radna". */
  Male: boolean;
  CameraId: string | null;
  CameraPreset: number | null;
}

export interface ICouncilSpeakQueueItem {
  Id: string;
  CouncilorId: string;
  Label: string | null;
  Type: ECouncilSpeakType;
  CreationDate: string;
}

export interface ICouncilSessionConduct {
  Session: ICouncilSession | null;
  Presence: ICouncilPresence[];
  PresentCount: number;
  TotalCouncilorsCount: number;
  HasQuorum: boolean;
  ActiveAgendaItemId: string | null;
  ActiveVoting: ICouncilVoting | null;
  SpeakQueue: ICouncilSpeakQueueItem[];
}

export interface ISaveCouncilProtocolRequest {
  /** Uri świeżo wgranego pliku. Puste = plik bez zmian (klient nie zna Uri już podpiętego). */
  FileUri?: string | null;
  FileName?: string | null;
  /** Jawne odpięcie pliku protokołu od sesji. */
  RemoveFile?: boolean;
  IsPublished: boolean;
}

// ── Dziennik przebiegu sesji i protokół ─────────────────────────────────────

export interface ICouncilProtocolItem {
  Id: string;
  SessionId: string;
  AgendaItemId: string | null;
  Type: ECouncilProtocolItemType;
  TypeLabel: string | null;
  CouncilorId: string | null;
  GuestId: string | null;
  PersonLabel: string | null;
  /** „Radny" / „Radna" albo funkcja gościa. */
  PersonPrefix: string | null;
  Description: string | null;
  Date: string;
  VideoSeconds: number | null;
  /** Gotowe zdanie protokołu z podstawioną osobą — układa je backend. */
  Sentence: string | null;
  /** Wpis protokolanta, a nie zdarzenie odnotowane samoczynnie. */
  IsManual: boolean;
}

export interface ISaveCouncilProtocolItemRequest {
  Id?: string | null;
  /** Puste = bieżący omawiany punkt porządku obrad. */
  AgendaItemId?: string | null;
  Type: ECouncilProtocolItemType;
  CouncilorId?: string | null;
  GuestId?: string | null;
  Description?: string | null;
  VideoSeconds?: number | null;
}

/** Stan protokołu sesji: czy istnieje, czy wolno go złożyć. */
export interface ICouncilProtocolDocumentRef {
  /** EditorDocument z protokołem; null = jeszcze nie powstał. */
  DocumentId: string | null;
  GeneratedDate: string | null;
  CanGenerate: boolean;
  /** Wyjaśnienie, gdy nie wolno składać protokołu. */
  Reason: string | null;
  ItemsCount: number;
  /** W dzienniku pojawiły się wpisy nowsze niż dokument. */
  IsOutdated: boolean;
}

/** Rodzaje wpisów, które protokolant dodaje ręcznie — reszta powstaje z przebiegu obrad. */
export const MANUAL_PROTOCOL_TYPES: ECouncilProtocolItemType[] = [
  ECouncilProtocolItemType.PersonTookVote,
  ECouncilProtocolItemType.PersonSubmitAnRequest,
  ECouncilProtocolItemType.PersonSubmitAnObjection,
  ECouncilProtocolItemType.PersonSubmitAnInterpelation,
  ECouncilProtocolItemType.PersonSubmitAnQuestionn,
  ECouncilProtocolItemType.DiscussionBegin,
  ECouncilProtocolItemType.DiscussionEnd,
  ECouncilProtocolItemType.PresenceCheck,
  ECouncilProtocolItemType.Other,
];

/** Wpisy odnoszące się do konkretnej osoby — bez niej zdanie protokołu się nie domknie. */
export const PERSONAL_PROTOCOL_TYPES: ECouncilProtocolItemType[] = [
  ECouncilProtocolItemType.PersonTookVote,
  ECouncilProtocolItemType.PersonSubmitAnRequest,
  ECouncilProtocolItemType.PersonSubmitAnObjection,
  ECouncilProtocolItemType.PersonSubmitAnInterpelation,
  ECouncilProtocolItemType.PersonSubmitAnQuestionn,
];

/** Wpisy wymagające treści od protokolanta. */
export const DESCRIBED_PROTOCOL_TYPES: ECouncilProtocolItemType[] = [
  ...PERSONAL_PROTOCOL_TYPES,
  ECouncilProtocolItemType.Other,
];

export const protocolTypeLabel = (type: ECouncilProtocolItemType): string => {
  switch (type) {
    case ECouncilProtocolItemType.PersonTookVote:
      return "Wystąpienie";
    case ECouncilProtocolItemType.PersonSubmitAnRequest:
      return "Wniosek";
    case ECouncilProtocolItemType.PersonSubmitAnObjection:
      return "Sprzeciw";
    case ECouncilProtocolItemType.PersonSubmitAnInterpelation:
      return "Interpelacja";
    case ECouncilProtocolItemType.PersonSubmitAnQuestionn:
      return "Zapytanie";
    case ECouncilProtocolItemType.DiscussionBegin:
      return "Otwarcie dyskusji";
    case ECouncilProtocolItemType.DiscussionEnd:
      return "Zamknięcie dyskusji";
    case ECouncilProtocolItemType.PresenceCheck:
      return "Sprawdzenie obecności";
    case ECouncilProtocolItemType.Other:
      return "Inne zdarzenie";
    default:
      return "Zdarzenie obrad";
  }
};

/** Stany sesji, w których panel prowadzącego pozwala sterować obradami. */
export const isSessionRunning = (state: ECouncilSessionState): boolean =>
  state === ECouncilSessionState.InProgress ||
  state === ECouncilSessionState.PresenceCheck;
